import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid } from 'recharts';
import { Download, FileText, Trash2, X, Plus, LogOut, RefreshCw } from 'lucide-react';
import { api, apiErrorMessage, tokenStore, API_BASE } from '../lib/api';

const STATUS_LABELS = { new: 'Nuovo', in_review: 'In esame', engaged: 'Incaricato', declined: 'Declinato', closed: 'Chiuso' };
const STATUS_ORDER = ['new', 'in_review', 'engaged', 'declined', 'closed'];

export default function Admin() {
  const [user, setUser] = useState(tokenStore.getUser());
  const [tab, setTab] = useState('claims');

  // If token exists but no user, verify session
  useEffect(() => {
    if (tokenStore.get() && !user) {
      api.get('/auth/me').then((r) => { setUser(r.data); tokenStore.setUser(r.data); })
        .catch(() => { tokenStore.clear(); setUser(null); });
    }
  }, [user]);

  if (!user) return <Login onLoggedIn={setUser} />;

  const logout = () => {
    tokenStore.clear();
    setUser(null);
    toast.success('Disconnesso.');
  };

  const isAdmin = user.role === 'admin';

  return (
    <div className="min-h-screen bg-ink-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 py-6 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Studio Belliardo · Amministrazione</div>
          <h1 className="font-serif text-2xl font-light mt-1">Pannello di gestione</h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-white/60">
            <span className="text-white">{user.name}</span>
            <span className="mx-2 text-white/30">·</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{user.role}</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 hover:text-white inline-flex items-center gap-2">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.25} /> Esci
          </button>
        </div>
      </header>

      <div className="border-b border-white/10 px-6 md:px-10">
        <nav className="flex flex-wrap gap-6">
          {[
            ['claims', 'Richieste'],
            ['analytics', 'Analytics'],
            isAdmin && ['users', 'Utenti'],
            isAdmin && ['audit', 'Audit log'],
          ].filter(Boolean).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`py-4 border-b-2 text-[11px] font-mono uppercase tracking-[0.25em] transition-colors ${tab === k ? 'border-white text-white' : 'border-transparent text-white/50 hover:text-white'}`}>
              {l}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6 md:p-10">
        {tab === 'claims' && <ClaimsTab />}
        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'users' && isAdmin && <UsersTab currentUser={user} />}
        {tab === 'audit' && isAdmin && <AuditTab />}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Login
// -------------------------------------------------------------
function Login({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/auth/login', { email, password });
      tokenStore.set(r.data.token);
      tokenStore.setUser(r.data.user);
      onLoggedIn(r.data.user);
      toast.success('Accesso effettuato.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Credenziali non valide.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink-950 flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md bg-ink-900 border border-white/10 p-8 md:p-10 space-y-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">— Area Riservata</div>
          <h1 className="font-serif text-3xl text-white font-light">Accesso amministrazione</h1>
          <p className="text-sm text-white/60 mt-3">Studio Legale Belliardo · Verona</p>
        </div>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-1">— Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="w-full bg-transparent border-b border-white/15 text-white py-2 focus:outline-none focus:border-white" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-1">— Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-white/15 text-white py-2 focus:outline-none focus:border-white" />
        </label>
        <button type="submit" disabled={busy}
          data-testid="admin-login-btn"
          className="w-full bg-white text-black py-3 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300 disabled:opacity-50">
          {busy ? 'Accesso…' : 'Accedi'}
        </button>
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// Claims tab
// -------------------------------------------------------------
function ClaimsTab() {
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { setClaims((await api.get('/admin/claims')).data); }
    catch (e) { toast.error(apiErrorMessage(e, 'Errore caricamento.')); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/claims/${id}`, { status });
      toast.success('Stato aggiornato.');
      load();
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const downloadFile = async (claimId, file) => {
    try {
      const r = await api.get(`/admin/claims/${claimId}/files/${file.file_id}`, { responseType: 'blob' });
      triggerDownload(r.data, file.filename);
    } catch (e) { toast.error(apiErrorMessage(e, 'Download fallito.')); }
  };

  const exportPdf = async (claim) => {
    try {
      const r = await api.get(`/admin/claims/${claim.id}/pdf`, { responseType: 'blob' });
      triggerDownload(r.data, `feasibility-memo-${claim.id.slice(0, 8)}.pdf`);
      toast.success('Memo esportato.');
    } catch (e) { toast.error(apiErrorMessage(e, 'Export PDF fallito.')); }
  };

  const filtered = filter === 'all' ? claims : claims.filter((c) => c.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {['all', ...STATUS_ORDER].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.25em] border ${filter === s ? 'bg-white text-black border-white' : 'border-white/15 text-white/60 hover:text-white'}`}>
              {s === 'all' ? 'Tutte' : STATUS_LABELS[s]} · {s === 'all' ? claims.length : claims.filter((c) => c.status === s).length}
            </button>
          ))}
        </div>
        <button onClick={load} disabled={busy} data-testid="claims-refresh"
          className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 hover:text-white">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} strokeWidth={1.25} /> Ricarica
        </button>
      </div>

      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 bg-ink-900">
          <div className="col-span-2">Data</div>
          <div className="col-span-2">Paese</div>
          <div className="col-span-3">Azienda</div>
          <div className="col-span-3">Debitore</div>
          <div className="col-span-1">File</div>
          <div className="col-span-1">Stato</div>
        </div>
        {filtered.length === 0 && <div className="p-8 text-center text-white/40 text-sm" data-testid="admin-empty">Nessuna richiesta.</div>}
        {filtered.map((c) => (
          <button key={c.id} onClick={() => setSelected(c)} data-testid={`row-${c.id}`}
            className="w-full text-left grid grid-cols-12 gap-4 px-4 py-4 border-b border-white/5 text-sm hover:bg-ink-900 transition-colors">
            <div className="col-span-2 text-white/60 font-mono text-xs">
              {new Date(c.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
            <div className="col-span-2 text-white">{c.country}</div>
            <div className="col-span-3 text-white/80 truncate">{c.company_name}</div>
            <div className="col-span-3 text-white/60 truncate">{c.debtor_name}</div>
            <div className="col-span-1 text-white/50 font-mono text-xs">{(c.files || []).length}</div>
            <div className="col-span-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">{STATUS_LABELS[c.status] || c.status}</div>
          </button>
        ))}
      </div>

      {selected && (
        <ClaimDetail
          claim={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => updateStatus(selected.id, s)}
          onDownload={(f) => downloadFile(selected.id, f)}
          onExportPdf={() => exportPdf(selected)}
        />
      )}
    </div>
  );
}

function ClaimDetail({ claim, onClose, onStatus, onDownload, onExportPdf }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="admin-detail-modal"
        className="bg-ink-900 border border-white/10 max-w-3xl w-full max-h-[88vh] overflow-auto p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Richiesta · {claim.country}</div>
            <h2 className="font-serif text-3xl font-light text-white mt-1">{claim.company_name}</h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl">×</button>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
          {[
            ['Contatto', claim.contact_name], ['Email', claim.email], ['Telefono', claim.phone || '—'],
            ['Debitore', claim.debtor_name], ['Importo (EUR)', claim.claim_amount_eur || '—'],
            ['Fattura', claim.invoice_date || '—'], ['ID', claim.id],
            ['Ricevuta il', new Date(claim.created_at).toLocaleString('it-IT')],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{k}</dt>
              <dd className="text-white/90 mt-1 break-words">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 border-t border-white/10 pt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Sintesi caso</div>
          <p className="text-white/80 leading-relaxed whitespace-pre-wrap text-sm">{claim.case_summary}</p>
        </div>

        {claim.files?.length > 0 && (
          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Allegati · {claim.files.length}</div>
            <ul className="space-y-2">
              {claim.files.map((f) => (
                <li key={f.file_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white/80 truncate">{f.filename} <span className="text-white/40 ml-2">· {(f.size / 1024).toFixed(0)} KB</span></span>
                  <button onClick={() => onDownload(f)} data-testid={`file-download-${f.file_id}`}
                    className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 hover:text-white">
                    <Download className="h-3.5 w-3.5" strokeWidth={1.25} /> Scarica
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3 justify-between border-t border-white/10 pt-6">
          <div className="flex flex-wrap gap-2">
            {STATUS_ORDER.map((s) => (
              <button key={s} onClick={() => onStatus(s)} data-testid={`status-${s}`}
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-[0.25em] border ${claim.status === s ? 'bg-white text-black border-white' : 'border-white/15 text-white/70 hover:text-white'}`}>
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
          <button onClick={onExportPdf} data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-oxblood text-white text-[10px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood-hover">
            <FileText className="h-3.5 w-3.5" strokeWidth={1.25} /> Esporta memo PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Analytics
// -------------------------------------------------------------
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/analytics/summary').then((r) => setData(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  }, []);
  if (!data) return <div className="text-white/40 text-sm">Caricamento…</div>;

  return (
    <div data-testid="analytics-panel" className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {[
          ['Totale richieste', data.total],
          ['Mercati coperti', data.by_country.length],
          ['Tasso di incarico', `${data.engagement_rate}%`],
          ['Tasso di rifiuto', `${data.decline_rate}%`],
        ].map(([k, v]) => (
          <div key={k} className="bg-ink-900 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">— {k}</div>
            <div className="font-serif text-4xl text-white font-light">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-ink-900 border border-white/10 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">— Richieste per mercato</div>
          <div className="h-64" data-testid="chart-country">
            <ResponsiveContainer>
              <BarChart data={data.by_country}>
                <CartesianGrid strokeDasharray="2 4" stroke="#222" vertical={false} />
                <XAxis dataKey="country" tick={{ fill: '#888', fontSize: 11, fontFamily: 'JetBrains Mono' }} tickLine={false} axisLine={{ stroke: '#333' }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#333' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 0, fontFamily: 'IBM Plex Sans' }} />
                <Bar dataKey="count" fill="#4A0404" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-ink-900 border border-white/10 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">— Andamento ultimi 6 mesi</div>
          <div className="h-64" data-testid="chart-trend">
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="2 4" stroke="#222" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#333' }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#333' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 0 }} />
                <Line type="monotone" dataKey="count" stroke="#fff" strokeWidth={1.5} dot={{ fill: '#4A0404', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-ink-900 border border-white/10 p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">— Distribuzione per stato</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {STATUS_ORDER.map((s) => {
            const n = data.by_status.find((x) => x.status === s)?.count || 0;
            return (
              <div key={s} className="border border-white/10 p-4" data-testid={`stat-${s}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{STATUS_LABELS[s]}</div>
                <div className="font-serif text-3xl text-white font-light mt-2">{n}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Users
// -------------------------------------------------------------
function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try { setUsers((await api.get('/admin/users')).data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deactivate = async (id) => {
    if (!window.confirm('Disattivare questo utente?')) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('Utente disattivato.'); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}`, { role }); toast.success('Ruolo aggiornato.'); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div data-testid="users-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          {users.length} utenti totali · {users.filter((u) => u.active).length} attivi
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-user-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors">
          <Plus className="h-3.5 w-3.5" strokeWidth={1.25} /> Nuovo utente
        </button>
      </div>

      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 bg-ink-900">
          <div className="col-span-3">Nome</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Ruolo</div>
          <div className="col-span-2">Stato</div>
          <div className="col-span-1 text-right">Azioni</div>
        </div>
        {users.map((u) => (
          <div key={u.id} className="grid grid-cols-12 gap-4 px-4 py-4 border-b border-white/5 text-sm" data-testid={`user-row-${u.id}`}>
            <div className="col-span-3 text-white">{u.name}</div>
            <div className="col-span-4 text-white/70 truncate">{u.email}</div>
            <div className="col-span-2">
              <select value={u.role} onChange={(e) => {
                const newRole = e.target.value;
                if (newRole === u.role) return;
                if (!window.confirm(`Cambiare il ruolo di ${u.email} in "${newRole}"?`)) {
                  e.target.value = u.role;
                  return;
                }
                setRole(u.id, newRole);
              }}
                disabled={u.id === currentUser.id}
                data-testid={`user-role-${u.id}`}
                className="bg-transparent border border-white/15 text-white text-xs font-mono uppercase tracking-[0.2em] py-1 px-2 focus:outline-none focus:border-white disabled:opacity-50">
                <option value="admin" className="bg-ink-900">admin</option>
                <option value="desk" className="bg-ink-900">desk</option>
              </select>
            </div>
            <div className="col-span-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <span className={u.active ? 'text-white/70' : 'text-oxblood'}>{u.active ? 'Attivo' : 'Disattivato'}</span>
            </div>
            <div className="col-span-1 text-right">
              {u.active && u.id !== currentUser.id && (
                <button onClick={() => deactivate(u.id)} data-testid={`deactivate-${u.id}`}
                  className="text-white/40 hover:text-oxblood inline-flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.25} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { load(); setShowCreate(false); }} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'desk', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post('/admin/users', form); toast.success('Utente creato.'); onCreated(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="create-user-modal"
        className="bg-ink-900 border border-white/10 max-w-md w-full p-8 space-y-5">
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-2xl font-light text-white">Nuovo utente</h3>
          <button type="button" onClick={onClose} className="text-white/60"><X className="h-5 w-5" /></button>
        </div>
        {[
          ['Nome', 'name', 'text'],
          ['Email', 'email', 'email'],
          ['Password (min 8)', 'password', 'password'],
        ].map(([label, key, type]) => (
          <label className="block" key={key}>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-1">— {label}</span>
            <input type={type} required minLength={type === 'password' ? 8 : undefined} value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`new-user-${key}`}
              className="w-full bg-transparent border-b border-white/15 text-white py-2 focus:outline-none focus:border-white" />
          </label>
        ))}
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-1">— Ruolo</span>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="new-user-role"
            className="w-full bg-transparent border-b border-white/15 text-white py-2 focus:outline-none focus:border-white">
            <option value="desk" className="bg-ink-900">desk</option>
            <option value="admin" className="bg-ink-900">admin</option>
          </select>
        </label>
        <button type="submit" disabled={busy} data-testid="new-user-submit"
          className="w-full bg-white text-black py-3 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors disabled:opacity-50">
          {busy ? 'Creazione…' : 'Crea utente'}
        </button>
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// Audit log
// -------------------------------------------------------------
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [facets, setFacets] = useState({ actions: [], actors: [] });
  const [filters, setFilters] = useState({ actor_email: '', action: '', date_from: '', date_to: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/admin/audit-log?${params}`);
      setLogs(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/admin/audit-log/facets').then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const clearFilters = () => setFilters({ actor_email: '', action: '', date_from: '', date_to: '' });
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div data-testid="audit-panel">
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <select value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
          data-testid="audit-filter-actor"
          className="bg-transparent border border-white/15 text-white text-xs font-mono uppercase tracking-[0.2em] py-2 px-3 focus:outline-none focus:border-white">
          <option value="" className="bg-ink-900">Tutti gli utenti</option>
          {facets.actors.map((a) => <option key={a} value={a} className="bg-ink-900">{a}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          data-testid="audit-filter-action"
          className="bg-transparent border border-white/15 text-white text-xs font-mono uppercase tracking-[0.2em] py-2 px-3 focus:outline-none focus:border-white">
          <option value="" className="bg-ink-900">Tutte le azioni</option>
          {facets.actions.map((a) => <option key={a} value={a} className="bg-ink-900">{a}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          data-testid="audit-filter-from"
          className="bg-transparent border border-white/15 text-white text-xs font-mono py-2 px-3 focus:outline-none focus:border-white [color-scheme:dark]" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          data-testid="audit-filter-to"
          className="bg-transparent border border-white/15 text-white text-xs font-mono py-2 px-3 focus:outline-none focus:border-white [color-scheme:dark]" />
        <button onClick={clearFilters} disabled={!hasFilters}
          data-testid="audit-filter-clear"
          className="border border-white/15 text-white text-[10px] font-mono uppercase tracking-[0.2em] py-2 px-3 hover:bg-white hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-white">
          Reset filtri
        </button>
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
        {busy ? 'Aggiorno…' : `${logs.length} eventi`}{hasFilters ? ' · filtrati' : ''}
      </div>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 bg-ink-900">
          <div className="col-span-2">Data</div>
          <div className="col-span-3">Utente</div>
          <div className="col-span-2">Azione</div>
          <div className="col-span-3">Target</div>
          <div className="col-span-2">Dettaglio</div>
        </div>
        {logs.length === 0 && <div className="p-6 text-center text-white/40 text-sm" data-testid="audit-empty">Nessun evento con i filtri correnti.</div>}
        {logs.map((l) => (
          <div key={l.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/5 text-xs" data-testid={`audit-${l.id}`}>
            <div className="col-span-2 text-white/60 font-mono">{new Date(l.created_at).toLocaleString('it-IT')}</div>
            <div className="col-span-3 text-white/80 truncate">{l.actor_email}</div>
            <div className="col-span-2 font-mono uppercase tracking-[0.2em] text-white">{l.action}</div>
            <div className="col-span-3 text-white/60 truncate font-mono">{l.target || '—'}</div>
            <div className="col-span-2 text-white/60 truncate">{l.detail || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
